import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../apiClient';
import StakeholderProfileModal from '../components/stakeholder/StakeholderProfileModal';
import StakeholderGroupModal from '../components/stakeholder/StakeholderGroupModal';
import BulkImportModal from '../components/stakeholder/BulkImportModal';
import StakeholderAnalytics from '../components/stakeholder/StakeholderAnalytics';
import StakeholderCommunications from '../components/stakeholder/StakeholderCommunications';
import ProjectAssignmentModal from '../components/stakeholder/ProjectAssignmentModal';
import { FaPlus, FaSearch, FaFilter, FaUsers, FaUpload, FaEdit, FaTrash, FaChartBar, FaComments, FaBuilding, FaUserTie, FaRobot, FaLightbulb, FaExclamationTriangle, FaClipboardList, FaUser, FaLink, FaStore, FaBalanceScale, FaHandshake, FaGlobeAmericas, FaIndustry, FaUserFriends } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import './Stakeholders.css';

function Stakeholders({ apiBaseUrl }) {
  const [stakeholders, setStakeholders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStakeholders, setTotalStakeholders] = useState(0);
  const [analytics, setAnalytics] = useState(null);

  // Modals and views
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showCommunications, setShowCommunications] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // AI Analysis state
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [showProjectAnalysisModal, setShowProjectAnalysisModal] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [selectedProjectsForAnalysis, setSelectedProjectsForAnalysis] = useState([]);

  // Filters and search
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: '', // internal/external
    stakeholder_type: '',
    influence_level: '',
    interest_level: '',
    attitude: '',
    project_id: '',
    status: 'active'
  });
  const [showFilters, setShowFilters] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  
  // Available filter options
  const [filterOptions, setFilterOptions] = useState({
    stakeholder_types: [],
    influence_levels: ['high', 'medium', 'low'],
    interest_levels: ['high', 'medium', 'low'],
    categories: ['internal', 'external'],
    statuses: ['active', 'inactive', 'archived']
  });

  const fetchStakeholders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: 20,
        search: search.trim(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      // Use new comprehensive stakeholder API
      const response = await apiClient.get(`/v2/stakeholders?${params}`);
      setStakeholders(response.data.stakeholders || []);
      setTotalPages(response.data.pagination.pages || 1);
      setTotalStakeholders(response.data.pagination.total || 0);
      
      // Update filter options from API response
      if (response.data.filters) {
        setFilterOptions(prev => ({
          ...prev,
          ...response.data.filters
        }));
      }
    } catch (err) {
      console.error('Error fetching stakeholders:', err);
      setError('Failed to load stakeholders. Please try again.');
      setStakeholders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, filters]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get('/projects');
      // The projects API returns an array directly, not nested in a projects property
      setProjects(response.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]); // Set empty array on error to prevent undefined issues
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await apiClient.get('/v2/stakeholders/analytics');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  }, []);

  useEffect(() => {
    fetchStakeholders();
    fetchProjects();
    fetchAnalytics();
  }, [fetchStakeholders, fetchProjects, fetchAnalytics]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchStakeholders();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      stakeholder_type: '',
      influence_level: '',
      interest_level: '',
      attitude: '',
      project_id: '',
      status: 'active'
    });
    setSearch('');
    setCurrentPage(1);
  };

  const handleCreateStakeholder = () => {
    setSelectedStakeholder(null);
    setShowProfileModal(true);
  };

  const handleEditStakeholder = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setShowProfileModal(true);
  };

  const handleViewCommunications = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setShowCommunications(true);
  };

  const handleAssignToProject = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setShowAssignModal(true);
  };

  const handleProjectAssignment = async (stakeholderId, projectId) => {
    try {
      await apiClient.post(`/projects/${projectId}/stakeholders`, {
        stakeholder_id: stakeholderId
      });
      setShowAssignModal(false);
      // Show success message
      alert('Stakeholder successfully assigned to project!');
      fetchStakeholders(); // Refresh the list
    } catch (error) {
      console.error('Error assigning stakeholder to project:', error);
      alert('Failed to assign stakeholder to project. Please try again.');
    }
  };

  const handleDeleteStakeholder = async (stakeholder) => {
    if (window.confirm(`Are you sure you want to delete ${stakeholder.display_name || stakeholder.full_name}? This action cannot be undone.`)) {
      try {
        await apiClient.delete(`/v2/stakeholders/${stakeholder.id}`);
        fetchStakeholders();
        fetchAnalytics(); // Refresh analytics
      } catch (err) {
        console.error('Error deleting stakeholder:', err);
        setError('Failed to delete stakeholder. Please try again.');
      }
    }
  };

  const handleModalClose = (refreshNeeded) => {
    setShowProfileModal(false);
    setShowGroupModal(false);
    setShowImportModal(false);
    setShowCommunications(false);
    setShowAssignModal(false);
    setSelectedStakeholder(null);
    if (refreshNeeded) {
      fetchStakeholders();
      fetchAnalytics();
    }
  };

  // AI Analysis Functions
  const handleIndividualAIAnalysis = async (stakeholder) => {
    setAiAnalysisLoading(true);
    setAiAnalysisResult(null);
    setShowAIAnalysisModal(true);
    
    try {
      const response = await apiClient.post(`/v2/stakeholders/${stakeholder.id}/ai-profile-analysis`);
      setAiAnalysisResult({
        type: 'individual',
        stakeholder: stakeholder,
        analysis: response.data.analysis,
        metadata: response.data.metadata
      });
    } catch (err) {
      console.error('Error analyzing stakeholder:', err);
      setAiAnalysisResult({
        type: 'error',
        error: err.response?.data?.error || 'Failed to analyze stakeholder. Please try again.'
      });
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleProjectAIAnalysis = async (projectIds, analysisType = 'comprehensive') => {
    setAiAnalysisLoading(true);
    setAiAnalysisResult(null);
    setShowAIAnalysisModal(true);
    setShowProjectAnalysisModal(false);
    
    try {
      const response = await apiClient.post('/v2/stakeholders/ai-analysis', {
        analysis_type: analysisType,
        project_ids: projectIds
      });
      setAiAnalysisResult({
        type: 'project',
        analysis: response.data.analysis,
        metadata: response.data.metadata,
        projects: projectIds.map(id => projects.find(p => p.id === parseInt(id))).filter(Boolean)
      });
    } catch (err) {
      console.error('Error analyzing project stakeholders:', err);
      setAiAnalysisResult({
        type: 'error',
        error: err.response?.data?.error || 'Failed to analyze stakeholders. Please try again.'
      });
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const openProjectAnalysisModal = () => {
    setSelectedProjectsForAnalysis([]);
    setShowProjectAnalysisModal(true);
  };

  const handleProjectAnalysisSubmit = () => {
    if (selectedProjectsForAnalysis.length === 0) {
      alert('Please select at least one project for analysis.');
      return;
    }
    handleProjectAIAnalysis(selectedProjectsForAnalysis);
  };

  const getInfluenceColor = (level) => {
    switch (level) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getInterestColor = (level) => {
    switch (level) {
      case 'high': return '#17a2b8';
      case 'medium': return '#fd7e14';
      case 'low': return '#20c997';
      default: return '#6c757d';
    }
  };

  const getAttitudeColor = (attitude) => {
    switch (attitude) {
      case 'supporter': return '#28a745';
      case 'neutral': return '#ffc107';
      case 'resistant': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStakeholderIcon = (stakeholder) => {
    if (stakeholder.stakeholder_category === 'external') {
      switch (stakeholder.stakeholder_type) {
        case 'customer': return FaStore;
        case 'vendor': return FaIndustry;
        case 'regulatory': return FaBalanceScale;
        case 'community': return FaGlobeAmericas;
        case 'partner': return FaHandshake;
        case 'contractor': return FaBuilding;
        case 'investor': return FaChartBar;
        default: return FaBuilding;
      }
    }
    
    // Internal stakeholders with more variation
    if (stakeholder.stakeholder_type) {
      switch (stakeholder.stakeholder_type) {
        case 'employee': return FaUser;
        case 'manager': return FaUserTie;
        case 'executive': return FaUserTie;
        case 'team_lead': return FaUserFriends;
        default: return FaUserTie;
      }
    }
    
    return FaUserTie; // Default for internal stakeholders
  };

  // Render AI Analysis Results
  const renderAIAnalysisResult = (result) => {
    if (!result) return null;

    if (result.type === 'error') {
      return (
        <div style={{ color: 'red', fontWeight: 600, padding: '1.5rem' }}>
          {result.error}
        </div>
      );
    }

    const analysis = result.analysis;
    if (!analysis) return null;

    // Handle different analysis types
    if (result.type === 'individual') {
      return renderIndividualAnalysis(analysis, result.stakeholder);
    } else {
      return renderGroupAnalysis(analysis);
    }
  };

  const renderIndividualAnalysis = (analysis, stakeholder) => {
    // Handle fallback text format
    if (analysis.format === 'text') {
      return (
        <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
              <FaUser style={{ marginRight: 8 }} />
              Analysis for {stakeholder?.display_name || stakeholder?.full_name}
            </h4>
            <div style={{ color: '#222', marginTop: 6, whiteSpace: 'pre-wrap' }}>{analysis.summary || 'No analysis available'}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
          <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
            <FaUser style={{ marginRight: 8 }} />
            Analysis for {stakeholder?.display_name || stakeholder?.full_name}
          </h4>
          <div style={{ color: '#222', marginTop: 6 }}>{analysis.stakeholder_assessment || 'No assessment available'}</div>
        </div>

        {analysis.strengths_opportunities && Array.isArray(analysis.strengths_opportunities) && analysis.strengths_opportunities.length > 0 && (
          <div style={{ background: '#e6f7f7', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #10b981' }}>
            <h4 style={{ margin: 0, color: '#10b981', fontWeight: 700 }}>
              <FaLightbulb style={{ marginRight: 8 }} />
              Strengths & Opportunities
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.strengths_opportunities.map((item, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.risks_concerns && Array.isArray(analysis.risks_concerns) && analysis.risks_concerns.length > 0 && (
          <div style={{ background: '#fff7e6', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #eab308' }}>
            <h4 style={{ margin: 0, color: '#eab308', fontWeight: 700 }}>
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              Risks & Concerns
            </h4>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {analysis.risks_concerns.map((item, idx) => (
                <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.engagement_strategy && (
          <div style={{ background: '#f3e8ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #a21caf' }}>
            <h4 style={{ margin: 0, color: '#a21caf', fontWeight: 700 }}>
              <FaUsers style={{ marginRight: 8 }} />
              Engagement Strategy
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>
              {analysis.engagement_strategy.recommended_approach && (
                <p><strong>Approach:</strong> {analysis.engagement_strategy.recommended_approach}</p>
              )}
              {analysis.engagement_strategy.frequency && (
                <p><strong>Frequency:</strong> {analysis.engagement_strategy.frequency}</p>
              )}
            </div>
          </div>
        )}

        {analysis.action_plan && (
          <div style={{ background: '#fffbe7', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #f59e42' }}>
            <h4 style={{ margin: 0, color: '#f59e42', fontWeight: 700 }}>
              <FaClipboardList style={{ marginRight: 8 }} />
              Action Plan
            </h4>
            {analysis.action_plan.immediate_steps && Array.isArray(analysis.action_plan.immediate_steps) && (
              <div style={{ marginTop: 8 }}>
                <p><strong>Immediate Steps:</strong></p>
                <ul style={{ paddingLeft: 20 }}>
                  {analysis.action_plan.immediate_steps.map((step, idx) => (
                    <li key={idx} style={{ color: '#222', marginBottom: 4 }}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroupAnalysis = (analysis) => {
    // Handle the actual backend response structure
    const summary = analysis.summary || analysis.executive_summary || analysis.engagement_summary || analysis.risk_summary;
    const champions = analysis.champions || [];
    const resistors = analysis.resistors || [];
    const keyRisks = analysis.key_risks || [];
    const actions = analysis.actions || [];
    const communications = analysis.communication || [];
    
    // Handle fallback text format
    if (analysis.format === 'text') {
      return (
        <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
              <HiSparkles style={{ marginRight: 8 }} />
              AI Analysis Results
            </h4>
            <div style={{ color: '#222', marginTop: 6, whiteSpace: 'pre-wrap' }}>{analysis.summary || 'No analysis available'}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="ai-analysis-result" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {summary && (
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #2563eb' }}>
            <h4 style={{ margin: 0, color: '#2563eb', fontWeight: 700 }}>
              <HiSparkles style={{ marginRight: 8 }} />
              Executive Summary
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>{summary}</div>
          </div>
        )}

        {(champions.length > 0 || resistors.length > 0) && (
          <div style={{ background: '#e6f7f7', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #10b981' }}>
            <h4 style={{ margin: 0, color: '#10b981', fontWeight: 700 }}>
              <FaUsers style={{ marginRight: 8 }} />
              Stakeholder Matrix Analysis
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>
              {champions.length > 0 && (
                <p><strong>Champions:</strong> {Array.isArray(champions) ? champions.join(', ') : champions}</p>
              )}
              {resistors.length > 0 && (
                <p><strong>Resistors:</strong> {Array.isArray(resistors) ? resistors.join(', ') : resistors}</p>
              )}
            </div>
          </div>
        )}

        {keyRisks.length > 0 && (
          <div style={{ background: '#fff7e6', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #eab308' }}>
            <h4 style={{ margin: 0, color: '#eab308', fontWeight: 700 }}>
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              Key Risks & Concerns
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>
              <ul style={{ paddingLeft: 20 }}>
                {keyRisks.map((risk, idx) => (
                  <li key={idx}>{risk}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {(actions.length > 0 || communications.length > 0) && (
          <div style={{ background: '#f3e8ff', borderRadius: 12, padding: '1.2rem 1.5rem', borderLeft: '6px solid #a21caf' }}>
            <h4 style={{ margin: 0, color: '#a21caf', fontWeight: 700 }}>
              <FaLightbulb style={{ marginRight: 8 }} />
              Recommendations
            </h4>
            <div style={{ color: '#222', marginTop: 6 }}>
              {actions.length > 0 && (
                <div>
                  <p><strong>Priority Actions:</strong></p>
                  <ul style={{ paddingLeft: 20 }}>
                    {actions.map((action, idx) => (
                      <li key={idx}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
              {communications.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <p><strong>Communication Strategies:</strong></p>
                  <ul style={{ paddingLeft: 20 }}>
                    {communications.map((comm, idx) => (
                      <li key={idx}>{comm}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStakeholderCard = (stakeholder) => {
    const IconComponent = getStakeholderIcon(stakeholder);
    
    return (
      <div key={stakeholder.id} className="stakeholder-card">
        <div className="stakeholder-card-header">
          <div className="stakeholder-info">
            <div className="stakeholder-name-row">
              <IconComponent className={`stakeholder-icon ${stakeholder.stakeholder_category}`} />
              <div>
                <h4>{stakeholder.display_name || stakeholder.full_name}</h4>
                <p className="stakeholder-position">{stakeholder.job_title}</p>
                {stakeholder.organization && (
                  <p className="stakeholder-org">{stakeholder.organization}</p>
                )}
              </div>
            </div>
            <div className="stakeholder-category">
              <span className={`category-badge ${stakeholder.stakeholder_category}`}>
                {stakeholder.stakeholder_category}
              </span>
              {stakeholder.stakeholder_type && (
                <span className="type-badge">
                  {stakeholder.stakeholder_type}
                </span>
              )}
            </div>
          </div>
          <div className="stakeholder-actions">
            <button 
              className="action-btn edit" 
              onClick={() => handleEditStakeholder(stakeholder)}
              title="Edit Profile"
            >
              <FaEdit />
            </button>
            <button 
              className="action-btn communications" 
              onClick={() => handleViewCommunications(stakeholder)}
              title="Communications"
            >
              <FaComments />
            </button>
            <button 
              className="action-btn assign" 
              onClick={() => handleAssignToProject(stakeholder)}
              title="Assign to Project"
            >
              <FaLink />
            </button>
            <button 
              className="action-btn ai-analysis" 
              onClick={() => handleIndividualAIAnalysis(stakeholder)}
              title="AI Analysis"
              disabled={aiAnalysisLoading}
            >
              <HiSparkles />
            </button>
            <button 
              className="action-btn delete" 
              onClick={() => handleDeleteStakeholder(stakeholder)}
              title="Delete"
            >
              <FaTrash />
            </button>
          </div>
        </div>
        
        <div className="stakeholder-card-body">
          <div className="stakeholder-details">
            <div className="detail-item">
              <span className="label">Email:</span>
              <span className="value">{stakeholder.email || 'N/A'}</span>
            </div>
            {stakeholder.department && (
              <div className="detail-item">
                <span className="label">Department:</span>
                <span className="value">{stakeholder.department}</span>
              </div>
            )}
            {stakeholder.phone && (
              <div className="detail-item">
                <span className="label">Phone:</span>
                <span className="value">{stakeholder.phone}</span>
              </div>
            )}
            {stakeholder.location && (
              <div className="detail-item">
                <span className="label">Location:</span>
                <span className="value">{stakeholder.location}</span>
              </div>
            )}
          </div>

          <div className="stakeholder-levels">
            {stakeholder.influence_level && (
              <div className="level-indicator">
                <span className="level-label">Influence:</span>
                <span 
                  className="level-badge" 
                  style={{ backgroundColor: getInfluenceColor(stakeholder.influence_level) }}
                >
                  {stakeholder.influence_level}
                </span>
              </div>
            )}
            {stakeholder.interest_level && (
              <div className="level-indicator">
                <span className="level-label">Interest:</span>
                <span 
                  className="level-badge" 
                  style={{ backgroundColor: getInterestColor(stakeholder.interest_level) }}
                >
                  {stakeholder.interest_level}
                </span>
              </div>
            )}
            {stakeholder.attitude && (
              <div className="level-indicator">
                <span className="level-label">Attitude:</span>
                <span 
                  className="level-badge" 
                  style={{ backgroundColor: getAttitudeColor(stakeholder.attitude) }}
                >
                  {stakeholder.attitude}
                </span>
              </div>
            )}
          </div>

          {stakeholder.custom_tags && stakeholder.custom_tags.length > 0 && (
            <div className="stakeholder-tags">
              {stakeholder.custom_tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
              {stakeholder.custom_tags.length > 3 && (
                <span className="tag more">+{stakeholder.custom_tags.length - 3}</span>
              )}
            </div>
          )}

          {stakeholder.profile_completion !== undefined && (
            <div className="profile-completion">
              <div className="completion-bar">
                <div 
                  className="completion-fill" 
                  style={{ width: `${stakeholder.profile_completion}%` }}
                />
              </div>
              <span className="completion-text">{stakeholder.profile_completion}% complete</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStakeholderTable = () => (
    <div className="table-container">
      <table className="stakeholders-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Type</th>
            <th>Email</th>
            <th>Position</th>
            <th>Organization</th>
            <th>Influence</th>
            <th>Interest</th>
            <th>Attitude</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {stakeholders.map(stakeholder => {
            const IconComponent = getStakeholderIcon(stakeholder);
            return (
              <tr key={stakeholder.id}>
                <td>
                  <div className="name-cell">
                    <IconComponent className={`stakeholder-icon ${stakeholder.stakeholder_category}`} />
                    <div>
                      <strong>{stakeholder.display_name || stakeholder.full_name}</strong>
                      {stakeholder.profile_completion !== undefined && (
                        <small className="completion-small">{stakeholder.profile_completion}% complete</small>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`category-badge small ${stakeholder.stakeholder_category}`}>
                    {stakeholder.stakeholder_category}
                  </span>
                </td>
                <td>{stakeholder.stakeholder_type || '-'}</td>
                <td>{stakeholder.email || '-'}</td>
                <td>{stakeholder.job_title || '-'}</td>
                <td>{stakeholder.organization || '-'}</td>
                <td>
                  {stakeholder.influence_level && (
                    <span 
                      className="level-badge small" 
                      style={{ backgroundColor: getInfluenceColor(stakeholder.influence_level) }}
                    >
                      {stakeholder.influence_level}
                    </span>
                  )}
                </td>
                <td>
                  {stakeholder.interest_level && (
                    <span 
                      className="level-badge small" 
                      style={{ backgroundColor: getInterestColor(stakeholder.interest_level) }}
                    >
                      {stakeholder.interest_level}
                    </span>
                  )}
                </td>
                <td>
                  {stakeholder.attitude && (
                    <span 
                      className="level-badge small" 
                      style={{ backgroundColor: getAttitudeColor(stakeholder.attitude) }}
                    >
                      {stakeholder.attitude}
                    </span>
                  )}
                </td>
                <td>
                  <div className="table-actions">
                    <button 
                      className="action-btn edit" 
                      onClick={() => handleEditStakeholder(stakeholder)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="action-btn communications" 
                      onClick={() => handleViewCommunications(stakeholder)}
                      title="Communications"
                    >
                      <FaComments />
                    </button>
                    <button 
                      className="action-btn assign" 
                      onClick={() => handleAssignToProject(stakeholder)}
                      title="Assign to Project"
                    >
                      <FaLink />
                    </button>
                    <button 
                      className="action-btn ai-analysis" 
                      onClick={() => handleIndividualAIAnalysis(stakeholder)}
                      title="AI Analysis"
                      disabled={aiAnalysisLoading}
                    >
                      <HiSparkles />
                    </button>
                    <button 
                      className="action-btn delete" 
                      onClick={() => handleDeleteStakeholder(stakeholder)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="stakeholders-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Comprehensive Stakeholder Management</h2>
          <p>Manage internal and external stakeholder profiles, communications, and project relationships</p>
          {analytics && (
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{analytics.summary.total_stakeholders}</span>
                <span className="stat-label">Total Stakeholders</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{analytics.summary.internal_stakeholders}</span>
                <span className="stat-label">Internal</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{analytics.summary.external_stakeholders}</span>
                <span className="stat-label">External</span>
              </div>
            </div>
          )}
        </div>
        <div className="header-actions">
          <button 
            className="btn secondary ai-btn" 
            onClick={openProjectAnalysisModal}
            disabled={aiAnalysisLoading}
            title="AI Analysis for Project Stakeholders"
          >
            <HiSparkles /> AI Analysis
          </button>
          <button 
            className="btn secondary" 
            onClick={() => setShowImportModal(true)}
          >
            <FaUpload /> Import CSV
          </button>
          <button 
            className="btn secondary" 
            onClick={() => setShowGroupModal(true)}
          >
            <FaUsers /> Manage Groups
          </button>
          <button 
            className="btn primary" 
            onClick={handleCreateStakeholder}
          >
            <FaPlus /> Add Stakeholder
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search stakeholders by name, email, position, organization, or type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="btn search-btn">Search</button>
        </form>

        <div className="filter-controls">
          <button 
            className={`btn filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>
          
          <div className="view-toggle">
            <button 
              className={`btn view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button 
              className={`btn view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Category:</label>
                <select 
                  value={filters.category} 
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {filterOptions.categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Type:</label>
                <select 
                  value={filters.stakeholder_type} 
                  onChange={(e) => handleFilterChange('stakeholder_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  {filterOptions.stakeholder_types.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Project:</label>
                <select 
                  value={filters.project_id} 
                  onChange={(e) => handleFilterChange('project_id', e.target.value)}
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Influence:</label>
                <select 
                  value={filters.influence_level} 
                  onChange={(e) => handleFilterChange('influence_level', e.target.value)}
                >
                  <option value="">All Levels</option>
                  {filterOptions.influence_levels.map(level => (
                    <option key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Interest:</label>
                <select 
                  value={filters.interest_level} 
                  onChange={(e) => handleFilterChange('interest_level', e.target.value)}
                >
                  <option value="">All Levels</option>
                  {filterOptions.interest_levels.map(level => (
                    <option key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Attitude:</label>
                <select 
                  value={filters.attitude} 
                  onChange={(e) => handleFilterChange('attitude', e.target.value)}
                >
                  <option value="">All Attitudes</option>
                  <option value="supporter">Supporter</option>
                  <option value="neutral">Neutral</option>
                  <option value="resistant">Resistant</option>
                </select>
              </div>

              <button className="btn clear-filters" onClick={clearFilters}>
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <span className="results-count">
          {totalStakeholders} stakeholder{totalStakeholders !== 1 ? 's' : ''} found
        </span>
        {(search || Object.values(filters).some(v => v && v !== 'active')) && (
          <button className="btn link clear-search" onClick={clearFilters}>
            Clear search and filters
          </button>
        )}
      </div>

      {/* Content */}
      <div className="stakeholders-content">
        {loading ? (
          <div className="loading-state">
            <p>Loading stakeholders...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button className="btn primary" onClick={fetchStakeholders}>
              Try Again
            </button>
          </div>
        ) : stakeholders.length === 0 ? (
          <div className="empty-state">
            <FaUsers className="empty-icon" />
            <h3>No stakeholders found</h3>
            <p>
              {search || Object.values(filters).some(v => v && v !== 'active')
                ? 'Try adjusting your search criteria or filters.'
                : 'Get started by adding your first stakeholder.'}
            </p>
            <button className="btn primary" onClick={handleCreateStakeholder}>
              <FaPlus /> Add First Stakeholder
            </button>
          </div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="stakeholders-grid">
                {stakeholders.map(renderStakeholderCard)}
              </div>
            ) : (
              renderStakeholderTable()
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="btn pagination-btn" 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  className="btn pagination-btn" 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showProfileModal && (
        <StakeholderProfileModal
          isOpen={showProfileModal}
          stakeholder={selectedStakeholder}
          onClose={handleModalClose}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {showGroupModal && (
        <StakeholderGroupModal
          isOpen={showGroupModal}
          onClose={handleModalClose}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          isOpen={showImportModal}
          onClose={handleModalClose}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {showAnalytics && analytics && (
        <StakeholderAnalytics
          isOpen={showAnalytics}
          analytics={analytics}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {showCommunications && selectedStakeholder && (
        <StakeholderCommunications
          isOpen={showCommunications}
          stakeholder={selectedStakeholder}
          onClose={() => setShowCommunications(false)}
          apiBaseUrl={apiBaseUrl}
        />
      )}

      {/* AI Analysis Modal */}
      {showAIAnalysisModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 800 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                <FaRobot style={{ marginRight: 8, color: '#2563eb' }} />
                AI Stakeholder Analysis
                {aiAnalysisResult?.projects && (
                  <div style={{ fontSize: '0.9rem', fontWeight: 400, color: '#666', marginTop: '0.25rem' }}>
                    {aiAnalysisResult.projects.length === 1 
                      ? `Project: ${aiAnalysisResult.projects[0].name}`
                      : `${aiAnalysisResult.projects.length} Projects: ${aiAnalysisResult.projects.map(p => p.name).join(', ')}`
                    }
                  </div>
                )}
              </h3>
              <button className="close-button" onClick={() => setShowAIAnalysisModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {aiAnalysisLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <HiSparkles style={{ fontSize: '3rem', color: '#2563eb', marginBottom: '1rem' }} />
                  <p>Analyzing stakeholder data...</p>
                </div>
              ) : aiAnalysisResult ? (
                renderAIAnalysisResult(aiAnalysisResult)
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <HiSparkles style={{ fontSize: '3rem', color: '#2563eb', marginBottom: '1rem' }} />
                  <p>No analysis results available</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowAIAnalysisModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Analysis Selection Modal */}
      {showProjectAnalysisModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                <HiSparkles style={{ marginRight: 8, color: '#2563eb' }} />
                Select Projects for AI Analysis
              </h3>
              <button className="close-button" onClick={() => setShowProjectAnalysisModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '2rem' }}>
              <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                Select one or more projects to analyze their stakeholders and generate AI-powered insights and recommendations.
              </p>
              
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e1e5e9', borderRadius: 8, padding: '1rem' }}>
                {projects.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#888' }}>No projects available</p>
                ) : (
                  projects.map(project => (
                    <label key={project.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedProjectsForAnalysis.includes(project.id.toString())}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProjectsForAnalysis(prev => [...prev, project.id.toString()]);
                          } else {
                            setSelectedProjectsForAnalysis(prev => prev.filter(id => id !== project.id.toString()));
                          }
                        }}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <div>
                        <strong>{project.name}</strong>
                        {project.description && (
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>{project.description}</div>
                        )}
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                          Status: {project.status} | Phase: {project.project_phase}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
              
              {selectedProjectsForAnalysis.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#f8f9fa', borderRadius: 4 }}>
                  <strong>{selectedProjectsForAnalysis.length}</strong> project{selectedProjectsForAnalysis.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn secondary" 
                onClick={() => setShowProjectAnalysisModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn primary" 
                onClick={handleProjectAnalysisSubmit}
                disabled={selectedProjectsForAnalysis.length === 0}
              >
                <HiSparkles /> Analyze Selected Projects
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && selectedStakeholder && (
        <ProjectAssignmentModal
          stakeholder={selectedStakeholder}
          projects={projects}
          onClose={() => setShowAssignModal(false)}
          onAssign={handleProjectAssignment}
        />
      )}
    </div>
  );
}

export default Stakeholders;

